import Api from "@/api/endpoints";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logoutUser } from "@/features/auth/authSlice";

// LocalStorage key for storing selected company ID
const SELECTED_COMPANY_STORAGE_KEY = "tally_selected_company_id";

// Read on store init so Ctrl+R keeps the selected tally company
const getStoredCompanyId = () => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    if (stored == null || stored === "") return null;
    const n = Number(stored);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const initialStoredCompanyId = getStoredCompanyId();

// Async thunks for tally reports
export const fetchCompanyDashboard = createAsyncThunk(
  "tallyReports/fetchCompanyDashboard",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getCompanyDashboard(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch company dashboard"
      );
    }
  }
);

export const fetchManualCompanyPrimarySales = createAsyncThunk(
  "tallyReports/fetchManualCompanyPrimarySales",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManualCompanyPrimarySales(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch manual company primary sales"
      );
    }
  }
);

export const fetchManufacturerDashboard = createAsyncThunk(
  "tallyReports/fetchManufacturerDashboard",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturerDashboard(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch manufacturer dashboard"
      );
    }
  }
);

export const fetchManufacturerSummary = createAsyncThunk(
  "tallyReports/fetchManufacturerSummary",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturerSummary(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch manufacturer summary"
      );
    }
  }
);

export const fetchAdminDashboard = createAsyncThunk(
  "tallyReports/fetchAdminDashboard",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getAdminDashboard(params);
      return response;
    } catch (error) {
      console.error("[Redux] Admin dashboard error:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch admin dashboard"
      );
    }
  }
);

export const fetchAdminSummary = createAsyncThunk(
  "tallyReports/fetchAdminSummary",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getAdminSummary(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch admin summary");
    }
  }
);

export const fetchManufacturerRankings = createAsyncThunk(
  "tallyReports/fetchManufacturerRankings",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturerRankings(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch manufacturer rankings"
      );
    }
  }
);

export const fetchAdminRankings = createAsyncThunk(
  "tallyReports/fetchAdminRankings",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getAdminRankings(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch admin rankings");
    }
  }
);

// New KPI fetch thunks
export const fetchKPIs = createAsyncThunk(
  "tallyReports/fetchKPIs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getKPIs(params);
      return response;
    } catch (error) {
      console.error("[Redux] KPIs error:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch KPIs"
      );
    }
  }
);

export const fetchManufacturerKPIs = createAsyncThunk(
  "tallyReports/fetchManufacturerKPIs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturerKPIs(params);
      return response;
    } catch (error) {
      console.error("[Redux] Manufacturer KPIs error:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch manufacturer KPIs"
      );
    }
  }
);

export const fetchDistributorKPIs = createAsyncThunk(
  "tallyReports/fetchDistributorKPIs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getDistributorKPIs(params);
      return response;
    } catch (error) {
      console.error("[Redux] Distributor KPIs error:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch distributor KPIs"
      );
    }
  }
);

export const fetchAdminKPIs = createAsyncThunk(
  "tallyReports/fetchAdminKPIs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getAdminKPIs(params);
      return response;
    } catch (error) {
      console.error("[Redux] Admin KPIs error:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch admin KPIs"
      );
    }
  }
);

/** Logged-in user's rows from sales_team_mapping (scoped distributor names). */
export const fetchSalesTeamMyDistributors = createAsyncThunk(
  "tallyReports/fetchSalesTeamMyDistributors",
  async (arg = {}, { rejectWithValue }) => {
    try {
      if (arg?.bypassForOwner === true) {
        return { distributors: [], bypassForOwner: true };
      }
      const response = await Api.salesTeamMapping.myDistributors();
      const inner = response?.data ?? response;
      return inner && typeof inner === "object" ? inner : { distributors: [] };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to load mapped distributors"
      );
    }
  }
);

const normName = (s) => String(s ?? "").trim().toLowerCase();

const initialState = {
  // Dashboard data
  dashboardData: null,
  summaryData: null,

  // New KPI data (from /api/dms/kpi endpoints)
  kpiData: null,
  isKPILoading: false,
  kpiError: null,

  // Manual (Excel) company primary sales
  manualPrimarySalesData: null,
  isManualPrimarySalesLoading: false,
  manualPrimarySalesError: null,

  // Loading states
  isLoading: false,
  isSummaryLoading: false,

  // Error states
  error: null,
  summaryError: null,

  // Selected company ID for company switcher (used across all DMS pages)
  // Hydrated from localStorage so refresh does not fall back to parent company
  selectedCompanyId: initialStoredCompanyId,
  isCompanySelected: initialStoredCompanyId != null,
  // Selected IDs for manufacturer/distributor dashboard dropdowns
  selectedDistributorId: null, // For manufacturer dashboard
  selectedOutletId: null, // For both manufacturer and distributor dashboards
  selectedSupplierId: null, // For distributor dashboard (manufacturer/supplier)
  selectedSalesUserId: [], // For sales user filter (persists across pages, supports multi-select)
  selectedSalesRoleId: null,

  // Distributor name for navigation from hierarchy to SKU page
  skuDistributorName: "",

  // sales_team_mapping: restrict distributor dropdowns / filters to these names (when non-empty)
  salesTeamDistributorAllowlist: [],
  salesTeamScopeFetchStatus: "idle",

  showCreditDebitNotes: false,

  showPrimarySales: (() => {
    if (typeof window === "undefined") return true;
    try {
      const stored = localStorage.getItem("show_primary_sales");
      return stored !== null ? stored === "true" : true;
    } catch {
      return true;
    }
  })(),

  // Filters
  filters: {
    companyId: initialStoredCompanyId ?? "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    manufacturerName: "",
    outletName: "",
    topN: 10,
    bottomN: 10,
    // New filters for dashboard
    timeRange: "30", // Last 30 Days
    manufacturerId: "",
    distributorId: "",
    outletId: "",
    pincode: "",
    companyIds: [], // For admin dashboard - array of company IDs
    comparisonPeriod: "", // No comparison by default
    search: "", // Search filter for persistence across pages
    categoryIds: "",
    subcategoryIds: "",
  },
  activeTags: [],
  filterBarCollapsed: false,
};

const tallyReportsSlice = createSlice({
  name: "tallyReports",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.summaryError = null;
    },
    // Default to first subsidiary (or parent) when a company hierarchy is provided
    setDefaultCompanyFromHierarchy: (state, action) => {
      const hierarchy = action.payload;
      const firstSubsidiaryId = hierarchy?.subsidiaries?.[0]?.id ?? null;
      const fallbackParentId = hierarchy?.id ?? null;
      const chosenId = firstSubsidiaryId || fallbackParentId;

      if (chosenId) {
        state.selectedCompanyId = chosenId;
        state.filters.companyId = chosenId;
      }
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      // filters updated
    },
    setActiveTags: (state, action) => {
      state.activeTags = action.payload;
    },
    setFilterBarCollapsed: (state, action) => {
      state.filterBarCollapsed = action.payload;
    },
    setSelectedCompanyId: (state, action) => {
      state.selectedCompanyId = action.payload;
      // Mark company as selected once it's set
      if (action.payload) {
        state.isCompanySelected = true;
      } else {
        state.isCompanySelected = false;
      }
      // Also update filters.companyId when selected company changes
      if (action.payload) {
        state.filters.companyId = action.payload;
        // Save to localStorage
        if (typeof window !== "undefined") {
          try {
            localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, String(action.payload));
          } catch (error) {
            console.warn("Failed to save selected company to localStorage:", error);
          }
        }
        // selectedCompanyId set
      } else {
        // Clear from localStorage if no company selected
        if (typeof window !== "undefined") {
          try {
            localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
          } catch (error) {
            console.warn("Failed to remove selected company from localStorage:", error);
          }
        }
      }
    },
    // Action to mark company selection as initialized (used by CompanySwitcher)
    setCompanySelected: (state, action) => {
      state.isCompanySelected = action.payload !== undefined ? action.payload : true;
    },
    setSelectedDistributorId: (state, action) => {
      state.selectedDistributorId = action.payload;
      // Also update filters.distributorId when selected distributor changes
      if (action.payload) {
        state.filters.distributorId = action.payload;
      } else {
        state.filters.distributorId = "";
      }
    },
    setSelectedOutletId: (state, action) => {
      state.selectedOutletId = action.payload;
      // Also update filters.outletId when selected outlet changes
      if (action.payload) {
        state.filters.outletId = action.payload;
      } else {
        state.filters.outletId = "";
      }
    },
    setSelectedSupplierId: (state, action) => {
      state.selectedSupplierId = action.payload;
      // Also update filters.manufacturerId when selected supplier changes
      if (action.payload) {
        state.filters.manufacturerId = action.payload;
      } else {
        state.filters.manufacturerId = "";
      }
    },
    setSelectedSalesUserId: (state, action) => {
      const payload = action.payload;
      if (payload == null) {
        state.selectedSalesUserId = [];
      } else if (Array.isArray(payload)) {
        state.selectedSalesUserId = payload;
      } else {
        state.selectedSalesUserId = payload ? [payload] : [];
      }
    },
    setSelectedSalesRoleId: (state, action) => {
      state.selectedSalesRoleId = action.payload ?? null;
    },
    setSkuDistributorName: (state, action) => {
      state.skuDistributorName = action.payload ?? "";
    },
    clearFilters: (state) => {
      state.filters = {
        companyId: "",
        startDate: null,
        endDate: null,
        state: "",
        district: "",
        city: "",
        distributorName: "",
        manufacturerName: "",
        outletName: "",
        topN: 10,
        bottomN: 10,
        timeRange: "30",
        manufacturerId: "",
        distributorId: "",
        outletId: "",
        pincode: "",
        companyIds: [],
        comparisonPeriod: "",
        search: "",
      };
      // Also clear dropdown selections when filters are cleared
      state.selectedDistributorId = null;
      state.selectedOutletId = null;
      state.selectedSupplierId = null;
      state.selectedSalesUserId = [];
      // Clear active tags
      state.activeTags = [];
    },
    clearDashboardData: (state) => {
      state.dashboardData = null;
      state.summaryData = null;
      state.adminSummaryData = null;
    },
    setShowCreditDebitNotes: (state, action) => {
      state.showCreditDebitNotes = action.payload;
    },
    setShowPrimarySales: (state, action) => {
      state.showPrimarySales = action.payload;
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("show_primary_sales", String(action.payload));
        } catch (error) {
          console.warn("Failed to save showPrimarySales to localStorage:", error);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch company dashboard (auto-detects role)
      .addCase(fetchCompanyDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.error = null;
      })
      .addCase(fetchCompanyDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch manual (Excel) company primary sales
      .addCase(fetchManualCompanyPrimarySales.pending, (state) => {
        state.isManualPrimarySalesLoading = true;
        state.manualPrimarySalesError = null;
      })
      .addCase(fetchManualCompanyPrimarySales.fulfilled, (state, action) => {
        state.isManualPrimarySalesLoading = false;
        state.manualPrimarySalesData = action.payload;
        state.manualPrimarySalesError = null;
      })
      .addCase(fetchManualCompanyPrimarySales.rejected, (state, action) => {
        state.isManualPrimarySalesLoading = false;
        state.manualPrimarySalesError = action.payload;
      })
      // Clear selectedCompanyId on logout
      .addCase(logoutUser.fulfilled, (state) => {
        // Reset all state to initial values
        state.dashboardData = null;
        state.summaryData = null;
        state.adminSummaryData = null;
        state.kpiData = null;
        state.isLoading = false;
        state.isSummaryLoading = false;
        state.isAdminSummaryLoading = false;
        state.isKPILoading = false;
        state.isRankingsLoading = false;
        state.error = null;
        state.summaryError = null;
        state.adminSummaryError = null;
        state.kpiError = null;
        state.rankingsError = null;
        state.rankingsData = null;
        state.selectedCompanyId = null;
        state.isCompanySelected = false;
        state.selectedDistributorId = null;
        state.selectedOutletId = null;
        state.selectedSupplierId = null;
        state.salesTeamDistributorAllowlist = [];
        state.salesTeamScopeFetchStatus = "idle";
        state.filters = {
          companyId: "",
          startDate: null,
          endDate: null,
          state: "",
          district: "",
          city: "",
          distributorName: "",
          topN: 10,
          bottomN: 10,
          timeRange: "30",
          manufacturerId: "",
          distributorId: "",
          outletId: "",
          pincode: "",
          companyIds: [],
          comparisonPeriod: "",
          search: "",
        };
        // Clear localStorage for selected company ID
        if (typeof window !== "undefined") {
          try {
            localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
          } catch (error) {
            console.warn("Failed to remove selected company from localStorage on logout:", error);
          }
        }
      })

      // Fetch manufacturer dashboard
      .addCase(fetchManufacturerDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManufacturerDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.error = null;
      })
      .addCase(fetchManufacturerDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch manufacturer summary
      .addCase(fetchManufacturerSummary.pending, (state) => {
        state.isSummaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchManufacturerSummary.fulfilled, (state, action) => {
        state.isSummaryLoading = false;
        state.summaryData = action.payload;
        state.summaryError = null;
      })
      .addCase(fetchManufacturerSummary.rejected, (state, action) => {
        state.isSummaryLoading = false;
        state.summaryError = action.payload;
      })

      // Fetch admin dashboard
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch admin summary
      .addCase(fetchAdminSummary.pending, (state) => {
        state.isAdminSummaryLoading = true;
        state.adminSummaryError = null;
      })
      .addCase(fetchAdminSummary.fulfilled, (state, action) => {
        state.isAdminSummaryLoading = false;
        state.adminSummaryData = action.payload;
        state.adminSummaryError = null;
      })
      .addCase(fetchAdminSummary.rejected, (state, action) => {
        state.isAdminSummaryLoading = false;
        state.adminSummaryError = action.payload;
      })

      // Fetch manufacturer rankings
      .addCase(fetchManufacturerRankings.pending, (state) => {
        state.isRankingsLoading = true;
        state.rankingsError = null;
      })
      .addCase(fetchManufacturerRankings.fulfilled, (state, action) => {
        state.isRankingsLoading = false;
        state.rankingsData = action.payload;
        state.rankingsError = null;
      })
      .addCase(fetchManufacturerRankings.rejected, (state, action) => {
        state.isRankingsLoading = false;
        state.rankingsError = action.payload;
      })

      // Fetch admin rankings
      .addCase(fetchAdminRankings.pending, (state) => {
        state.isRankingsLoading = true;
        state.rankingsError = null;
      })
      .addCase(fetchAdminRankings.fulfilled, (state, action) => {
        state.isRankingsLoading = false;
        state.rankingsData = action.payload;
        state.rankingsError = null;
      })
      .addCase(fetchAdminRankings.rejected, (state, action) => {
        state.isRankingsLoading = false;
        state.rankingsError = action.payload;
      })

      // Fetch KPIs (auto-detect role)
      .addCase(fetchKPIs.pending, (state) => {
        state.isKPILoading = true;
        state.kpiError = null;
      })
      .addCase(fetchKPIs.fulfilled, (state, action) => {
        state.isKPILoading = false;
        state.kpiData = action.payload;
        state.kpiError = null;
      })
      .addCase(fetchKPIs.rejected, (state, action) => {
        state.isKPILoading = false;
        state.kpiError = action.payload;
      })

      // Fetch manufacturer KPIs
      .addCase(fetchManufacturerKPIs.pending, (state) => {
        state.isKPILoading = true;
        state.kpiError = null;
      })
      .addCase(fetchManufacturerKPIs.fulfilled, (state, action) => {
        state.isKPILoading = false;
        state.kpiData = action.payload;
        state.kpiError = null;
      })
      .addCase(fetchManufacturerKPIs.rejected, (state, action) => {
        state.isKPILoading = false;
        state.kpiError = action.payload;
      })

      // Fetch distributor KPIs
      .addCase(fetchDistributorKPIs.pending, (state) => {
        state.isKPILoading = true;
        state.kpiError = null;
      })
      .addCase(fetchDistributorKPIs.fulfilled, (state, action) => {
        state.isKPILoading = false;
        state.kpiData = action.payload;
        state.kpiError = null;
      })
      .addCase(fetchDistributorKPIs.rejected, (state, action) => {
        state.isKPILoading = false;
        state.kpiError = action.payload;
      })

      // Fetch admin KPIs
      .addCase(fetchAdminKPIs.pending, (state) => {
        state.isKPILoading = true;
        state.kpiError = null;
      })
      .addCase(fetchAdminKPIs.fulfilled, (state, action) => {
        state.isKPILoading = false;
        state.kpiData = action.payload;
        state.kpiError = null;
      })
      .addCase(fetchAdminKPIs.rejected, (state, action) => {
        state.isKPILoading = false;
        state.kpiError = action.payload;
      })

      .addCase(fetchSalesTeamMyDistributors.pending, (state) => {
        state.salesTeamScopeFetchStatus = "loading";
      })
      .addCase(fetchSalesTeamMyDistributors.fulfilled, (state, action) => {
        state.salesTeamScopeFetchStatus = "succeeded";
        if (action.payload?.bypassForOwner === true) {
          state.salesTeamDistributorAllowlist = [];
          return;
        }
        const rows = action.payload?.distributors ?? [];
        const names = [...new Set(rows.map((r) => r.distributor_name).filter(Boolean))];
        state.salesTeamDistributorAllowlist = names;

        if (names.length === 0) {
          return;
        }

        const allowNorm = new Set(names.map(normName));
        const curName = state.filters.distributorName;
        const curTokens = String(curName || "")
          .split(/(?<!\\),/)
          .map((s) => s.replace(/\\,/g, ",").trim())
          .filter(Boolean);
        const keptTokens = curTokens.filter((t) => allowNorm.has(normName(t)));
        const curOk =
          curTokens.length > 0 && keptTokens.length === curTokens.length;

        if (curOk) {
          return;
        }

        if (keptTokens.length > 0) {
          // Multi-select: keep only allowlisted tokens (do not collapse to first).
          state.filters.distributorName = keptTokens
            .map((t) => String(t).replace(/,/g, "\\,"))
            .join(",");
          state.filters.distributorId = "";
          state.selectedDistributorId = null;
        } else if (names.length === 1) {
          const only = names[0];
          state.filters.distributorName = only;
          state.filters.distributorId = "";
          state.selectedDistributorId = only;
        } else if (curTokens.length === 0 && names.length > 1) {
          // No selection yet — leave empty so user can multi-select; do not force first.
          return;
        } else {
          // Previous selection fully outside allowlist — clear rather than force first.
          state.filters.distributorName = "";
          state.filters.distributorId = "";
          state.selectedDistributorId = null;
        }
      })
      .addCase(fetchSalesTeamMyDistributors.rejected, (state) => {
        state.salesTeamScopeFetchStatus = "failed";
        state.salesTeamDistributorAllowlist = [];
      });
  },
});

export const {
  clearError,
  setFilters,
  setActiveTags,
  setFilterBarCollapsed,
  setSelectedCompanyId,
  setCompanySelected,
  setSelectedDistributorId,
  setSelectedOutletId,
  setSelectedSupplierId,
  setSelectedSalesUserId,
  setSelectedSalesRoleId,
  setSkuDistributorName,
  clearFilters,
  clearDashboardData,
  setShowCreditDebitNotes,
  setShowPrimarySales,
} = tallyReportsSlice.actions;
export default tallyReportsSlice.reducer;
